"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { UserPlus, Loader2, CheckCircle, ChevronDown, Eye, EyeOff } from "lucide-react";
import CountrySelectModal, { COUNTRIES_STATIC, useCountry } from "@/components/CountrySelectModal";
import { SocialProviderButton, type SocialProvider } from "@/components/auth/SocialProviderButton";
import { notifySessionChanged } from "@/lib/auth/session";
import { authApi } from "@/lib/api/auth";

const RAW_API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
const LOCAL_API_PORT = "8000";

function isLocalHost(hostname: string): boolean {
    return hostname === "localhost" || hostname === "127.0.0.1";
}

function resolveApiBaseUrl(): string {
    try {
        const parsed = new URL(RAW_API_BASE);
        if (typeof window !== "undefined") {
            const runtimeHost = window.location.hostname;
            if (isLocalHost(parsed.hostname) && runtimeHost && !isLocalHost(runtimeHost)) {
                parsed.hostname = runtimeHost;
                parsed.port = LOCAL_API_PORT;
            }
        }
        return parsed.origin;
    } catch {
        return RAW_API_BASE;
    }
}

function PasswordStrength({ password }: { password: string }) {
    const hasLength = password.length >= 8;
    const hasLetter = /[A-Za-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const score = [hasLength, hasLetter, hasDigit, hasSpecial].filter(Boolean).length;

    if (!password) return null;

    const colors = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-green-500"];
    const labels = ["ضعيفة جداً", "ضعيفة", "متوسطة", "قوية"];

    return (
        <div className="mt-2">
            <div className="flex gap-1 mb-1">
                {[0, 1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-all ${i < score ? colors[score - 1] : "bg-white/20"}`}
                    />
                ))}
            </div>
            <p className={`text-xs ${score < 2 ? "text-red-400" : score < 4 ? "text-yellow-400" : "text-green-400"}`}>
                قوة كلمة المرور: {labels[score - 1] || "أدخل كلمة مرور"}
            </p>
        </div>
    );
}

export default function PublicRegisterPage() {
    const router = useRouter();
    const { setCountry } = useCountry();
    const apiBase = resolveApiBaseUrl();

    const [form, setForm] = useState({
        full_name: "",
        phone: "",
        email: "",
        password: "",
        confirm_password: "",
        city: "",
    });
    const [selectedCountry, setSelectedCountry] = useState(
        COUNTRIES_STATIC.find((country) => country.code === "OM") || COUNTRIES_STATIC[0]
    );
    const [countryModalOpen, setCountryModalOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [socialLoading, setSocialLoading] = useState<SocialProvider | null>(null);
    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [done, setDone] = useState(false);
    const [step, setStep] = useState<1 | 2 | 3>(1);

    const set = (key: string, value: string) => {
        setForm((p) => ({ ...p, [key]: value }));
        setFieldErrors((p) => ({ ...p, [key]: "" }));
        setError("");
    };

    const phoneDigitsCount = (value: string) => value.replace(/\D/g, "").length;

    const goNextStep = () => {
        setError("");
        if (step === 1) {
            const phone = form.phone.trim();
            if (!phone) {
                setFieldErrors((prev) => ({ ...prev, phone: "يرجى إدخال رقم الجوال." }));
                setError("يرجى إدخال البيانات المطلوبة ثم المتابعة.");
                return;
            }
            if (phoneDigitsCount(phone) < 7) {
                setFieldErrors((prev) => ({ ...prev, phone: "رقم الجوال غير صحيح. يرجى إدخال رقم صحيح." }));
                setError("يرجى تصحيح رقم الجوال ثم المتابعة.");
                return;
            }
            setStep(2);
            return;
        }

        if (step === 2) {
            const nextErrors: Record<string, string> = {};
            if (!form.password) nextErrors.password = "يرجى إدخال كلمة المرور.";
            else if (form.password.length < 8) nextErrors.password = "كلمة المرور يجب أن تكون 8 أحرف على الأقل.";
            else if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) {
                nextErrors.password = "كلمة المرور يجب أن تحتوي على حرف ورقم على الأقل.";
            }
            if (!form.confirm_password) nextErrors.confirm_password = "يرجى تأكيد كلمة المرور.";
            else if (form.password !== form.confirm_password) nextErrors.confirm_password = "كلمتا المرور غير متطابقتين.";
            if (Object.keys(nextErrors).length) {
                setFieldErrors((prev) => ({ ...prev, ...nextErrors }));
                setError("يرجى تصحيح كلمة المرور ثم المتابعة.");
                return;
            }
            setStep(3);
        }
    };

    const goPrevStep = () => {
        setError("");
        setStep((prev) => (prev > 1 ? ((prev - 1) as 1 | 2 | 3) : prev));
    };

    const completeAuthNavigation = (data: any, nameHint: string) => {
        localStorage.setItem("s360_access", data.access);
        localStorage.setItem("s360_refresh", data.refresh);
        if (data.user) localStorage.setItem("s360_user", JSON.stringify(data.user));
        notifySessionChanged();
        setCountry(selectedCountry);
        setDone(true);
        setTimeout(() => router.push("/customer-dashboard"), 1800);
        if (nameHint && !form.full_name) {
            setForm((prev) => ({ ...prev, full_name: nameHint }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setFieldErrors({});

        // Client-side confirmation check
        if (form.password !== form.confirm_password) {
            setFieldErrors({ confirm_password: "كلمتا المرور غير متطابقتين." });
            setStep(2);
            setLoading(false);
            return;
        }
        if (phoneDigitsCount(form.phone) < 7) {
            setFieldErrors({ phone: "رقم الجوال غير صحيح. يرجى إدخال رقم صحيح." });
            setStep(1);
            setLoading(false);
            return;
        }
        if (!form.full_name.trim()) {
            setFieldErrors({ full_name: "يرجى إدخال الاسم الكامل." });
            setStep(3);
            setLoading(false);
            return;
        }
        const normalizedPhoneLocal = form.phone
            .replace(/^\+/, "")
            .replace(/[\s()-]/g, "");

        try {
            const res = await fetch(`${apiBase}/api/auth/public-register/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    full_name: form.full_name,
                    phone: `${selectedCountry.phone_prefix}${normalizedPhoneLocal}`,
                    email: form.email || undefined,
                    password: form.password,
                    confirm_password: form.confirm_password,
                    country_code: selectedCountry.code,
                    city: form.city,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                // Map field errors
                const fe: Record<string, string> = {};
                if (data.phone) fe.phone = Array.isArray(data.phone) ? data.phone[0] : data.phone;
                if (data.email) fe.email = Array.isArray(data.email) ? data.email[0] : data.email;
                if (data.full_name) fe.full_name = Array.isArray(data.full_name) ? data.full_name[0] : data.full_name;
                if (data.password) fe.password = Array.isArray(data.password) ? data.password[0] : data.password;
                if (data.confirm_password) fe.confirm_password = Array.isArray(data.confirm_password) ? data.confirm_password[0] : data.confirm_password;
                if (Object.keys(fe).length) {
                    setFieldErrors(fe);
                    if (fe.phone) setStep(1);
                    else if (fe.password || fe.confirm_password) setStep(2);
                    else setStep(3);
                    setError("يرجى تصحيح البيانات المعلّمة ثم المحاولة مرة أخرى.");
                } else {
                    const firstValue = Object.values(data as Record<string, unknown>)[0];
                    const firstError = Array.isArray(firstValue) ? firstValue[0] : firstValue;
                    setError((data as any).non_field_errors?.[0] || (data as any).detail || String(firstError || "حدث خطأ. حاول مجدداً."));
                }
                return;
            }

            completeAuthNavigation(data, form.full_name);
        } catch {
            setError("خطأ في الاتصال بالخادم.");
        } finally {
            setLoading(false);
        }
    };

    const handleSocialAuth = (provider: SocialProvider) => {
        setError("");
        setSocialLoading(provider);
        try {
            authApi.startSocialOAuth({
                provider,
                role: "customer",
                full_name: form.full_name || undefined,
                email: form.email || undefined,
                phone: form.phone
                    ? `${selectedCountry.phone_prefix}${form.phone.replace(/^\+/, "").replace(/[\s()-]/g, "")}`
                    : undefined,
                country_code: selectedCountry.code,
                city: form.city || undefined,
                next: "/customer-dashboard",
            });
        } catch (err: any) {
            const msg =
                err?.message ||
                "تعذر المتابعة عبر التسجيل الاجتماعي حالياً.";
            setError(msg);
            setSocialLoading(null);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 flex items-center justify-center p-4">
                <div className="text-center">
                    <CheckCircle className="h-24 w-24 text-green-400 mx-auto mb-6 animate-bounce" />
                    <h2 className="text-2xl font-black text-white mb-2">مرحباً {form.full_name}!</h2>
                    <p className="text-orange-300">تم إنشاء حسابك بنجاح. يتم توجيهك...</p>
                </div>
            </div>
        );
    }

    const Field = ({ label, children, error: ferr }: { label: string; children: React.ReactNode; error?: string }) => (
        <div>
            <label className="block text-sm font-medium text-orange-200 mb-1.5">{label}</label>
            {children}
            {ferr && <p className="text-red-400 text-xs mt-1">{ferr}</p>}
        </div>
    );

    const inputClass = "w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition";

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 flex items-start md:items-center justify-center p-4 pt-8 pb-28 md:pb-8 overflow-y-auto">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/3 -left-24 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/3 -right-24 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
            </div>

            <div className="relative w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-500 rounded-3xl shadow-2xl shadow-orange-900/50 mb-4">
                        <UserPlus className="h-10 w-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-black text-white">إنشاء حساب عميل</h1>
                    <p className="text-orange-300 text-sm mt-1">انضم إلى منصة سيارتي 360</p>
                </div>

                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-3 gap-2">
                            {[1, 2, 3].map((s) => {
                                const isActive = step === s;
                                const isDone = step > s;
                                return (
                                    <div
                                        key={s}
                                        className={`rounded-xl py-2 text-center text-sm font-black border transition-all ${isActive
                                            ? "bg-orange-500/20 border-orange-400 text-orange-200"
                                            : isDone
                                                ? "bg-emerald-500/20 border-emerald-400 text-emerald-200"
                                                : "bg-white/5 border-white/15 text-white/60"
                                            }`}
                                    >
                                        {s}
                                    </div>
                                );
                            })}
                        </div>

                        <p className="text-center text-xs font-bold text-orange-200">
                            {step === 1
                                ? "الخطوة 1: رقم الجوال"
                                : step === 2
                                    ? "الخطوة 2: كلمة المرور"
                                    : "الخطوة 3: الاسم والبيانات"}
                        </p>

                        <div key={step} className="space-y-4 animate-in fade-in slide-in-from-right-3 duration-300">
                            {step === 1 && (
                                <>
                                    <Field label="رقم الجوال *" error={fieldErrors.phone}>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setCountryModalOpen(true)}
                                                className="bg-white/10 border border-white/20 text-white rounded-2xl px-3 py-3 text-sm flex items-center gap-1 min-w-[90px] hover:bg-white/15 transition shrink-0"
                                            >
                                                <span className="text-lg">{selectedCountry.emoji}</span>
                                                <span className="text-xs">{selectedCountry.phone_prefix}</span>
                                                <ChevronDown className="h-3 w-3 text-white/40" />
                                            </button>
                                            <input
                                                type="tel" value={form.phone} onChange={e => set("phone", e.target.value)} required dir="ltr"
                                                className="flex-1 bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
                                                placeholder="555 123 456"
                                            />
                                        </div>
                                    </Field>

                                    <Field label="الدولة *">
                                        <button
                                            type="button"
                                            onClick={() => setCountryModalOpen(true)}
                                            className="w-full bg-white/10 border border-white/20 text-white rounded-2xl px-4 py-3 text-sm flex items-center justify-between hover:bg-white/15 transition"
                                        >
                                            <span className="flex items-center gap-2">
                                                <span className="text-xl">{selectedCountry.emoji}</span>
                                                <span>{selectedCountry.name_ar}</span>
                                            </span>
                                            <ChevronDown className="h-4 w-4 text-white/50" />
                                        </button>
                                    </Field>
                                </>
                            )}

                            {step === 2 && (
                                <>
                                    <Field label="كلمة المرور *" error={fieldErrors.password}>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"} value={form.password}
                                                onChange={e => set("password", e.target.value)} required dir="ltr"
                                                name="new_password"
                                                autoComplete="new-password"
                                                autoCapitalize="none"
                                                autoCorrect="off"
                                                spellCheck={false}
                                                className={`${inputClass} pr-12`} placeholder="••••••••"
                                            />
                                            <button type="button" onClick={() => setShowPassword(v => !v)}
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition">
                                                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                            </button>
                                        </div>
                                        <PasswordStrength password={form.password} />
                                    </Field>

                                    <Field label="تأكيد كلمة المرور *" error={fieldErrors.confirm_password}>
                                        <div className="relative">
                                            <input
                                                type={showConfirm ? "text" : "password"} value={form.confirm_password}
                                                onChange={e => set("confirm_password", e.target.value)} required dir="ltr"
                                                name="confirm_new_password"
                                                autoComplete="new-password"
                                                autoCapitalize="none"
                                                autoCorrect="off"
                                                spellCheck={false}
                                                className={`${inputClass} pr-12`} placeholder="••••••••"
                                            />
                                            <button type="button" onClick={() => setShowConfirm(v => !v)}
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition">
                                                {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                            </button>
                                        </div>
                                    </Field>
                                </>
                            )}

                            {step === 3 && (
                                <>
                                    <Field label="الاسم الكامل *" error={fieldErrors.full_name}>
                                        <input
                                            type="text" value={form.full_name} onChange={e => set("full_name", e.target.value)} required
                                            className={inputClass} placeholder="مثال: محمد علي"
                                        />
                                    </Field>

                                    <Field label="المدينة (اختياري)">
                                        <input
                                            type="text" value={form.city} onChange={e => set("city", e.target.value)}
                                            className={inputClass} placeholder="مثال: الرياض"
                                        />
                                    </Field>

                                    <Field label="البريد الإلكتروني (اختياري)" error={fieldErrors.email}>
                                        <input
                                            type="email" value={form.email} onChange={e => set("email", e.target.value)} dir="ltr"
                                            className={inputClass} placeholder="example@email.com"
                                        />
                                    </Field>
                                </>
                            )}
                        </div>

                        {error && (
                            <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-3 text-red-300 text-sm text-center">
                                {error}
                            </div>
                        )}

                        <button
                            type={step === 3 ? "submit" : "button"}
                            onClick={step === 3 ? undefined : goNextStep}
                            disabled={loading}
                            className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-800 disabled:cursor-not-allowed text-white font-bold rounded-2xl py-3.5 text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-orange-900/50"
                        >
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                            {loading ? "جاري إنشاء الحساب..." : step === 3 ? "إنشاء الحساب" : "متابعة"}
                        </button>
                        {step > 1 && (
                            <button
                                type="button"
                                onClick={goPrevStep}
                                disabled={loading}
                                className="w-full border border-white/20 text-white/80 hover:text-white hover:bg-white/10 rounded-2xl py-2.5 text-sm font-bold transition"
                            >
                                رجوع
                            </button>
                        )}

                        <div className="pt-2 space-y-2">
                            <div className="relative">
                                <div className="h-px bg-white/20" />
                                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#0f172a] px-3 text-[11px] text-white/50 font-bold">
                                    أو عبر
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <SocialProviderButton
                                    provider="facebook"
                                    loadingProvider={socialLoading}
                                    disabled={loading}
                                    onClick={handleSocialAuth}
                                />
                                <SocialProviderButton
                                    provider="icloud"
                                    loadingProvider={socialLoading}
                                    disabled={loading}
                                    onClick={handleSocialAuth}
                                />
                                <SocialProviderButton
                                    provider="google"
                                    loadingProvider={socialLoading}
                                    disabled={loading}
                                    onClick={handleSocialAuth}
                                />
                            </div>
                        </div>
                    </form>

                    <p className="text-center text-white/40 text-xs mt-4">
                        لديك حساب بالفعل؟{" "}
                        <Link href="/login" className="text-blue-400 hover:underline">تسجيل الدخول</Link>
                        {" · "}
                        <Link href="/register" className="text-orange-400 hover:underline">سجّل كتاجر</Link>
                    </p>
                </div>
            </div>

            <CountrySelectModal
                isOpen={countryModalOpen}
                onClose={() => setCountryModalOpen(false)}
                onSelect={(c) => setSelectedCountry(c)}
            />
        </div>
    );
}
